import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Building, User, Mail, Lock, Phone, Globe, 
  Check, ChevronRight, ChevronLeft, AlertCircle, Eye, EyeOff 
} from 'lucide-react';
import companyService from '../../services/companyService';
import { useToast, ToastContainer } from '../common/Toast';
import CAM360Badge from '../common/CAM360Badge';
import HelpButton from '../common/HelpButton';

const CompanyOnboarding = ({ embedded = false, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { error: showToastError, success: showToastSuccess, toasts, removeToast } = useToast();

  const [formData, setFormData] = useState({
    companyId: '',
    companyName: '',
    companyType: 'service',
    domain: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    adminPhone: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await companyService.onboard(formData);
      if (onSuccess) {
        showToastSuccess?.('Tenant created successfully.');
        onSuccess();
      } else {
        navigate('/login', { state: { message: 'Workspace created successfully! Please login.' } });
      }
    } catch (err) {
      const msg = err.message || 'Onboarding failed. Please check your inputs.';
      setError(msg);
      showToastError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? 'cam-auth-page cam-embedded-onboarding' : 'cam-auth-page'} style={embedded ? { minHeight: 'auto', padding: 0 } : undefined}>
      <div className="cam-auth-container" style={{ maxWidth: embedded ? '100%' : '1000px' }}>
        <div className="cam-auth-card">
          
          {/* Left Section - Branding */}
          <div className="cam-auth-left">
            <div className="cam-brand-content">
              <div className="cam-brand-logo">
                <div className="cam-logo-icon" style={{ backgroundColor: 'transparent', boxShadow: 'none', padding: '8px' }}>
                  <CAM360Badge size={100} />
                </div>
                <h1 className="cam-brand-title">CAM360</h1>
                <p className="cam-brand-subtitle">Workspace Initialization</p>
              </div>
              
              <div className="cam-step-indicator mt-4">
                {[1, 2, 3].map((num) => (
                  <div key={num} className={`cam-step-item ${step === num ? 'active' : ''}`}>
                    <div className="cam-step-number">{num === 1 ? <Building size={14}/> : num === 2 ? <User size={14}/> : <Check size={14}/>}</div>
                    <span className="cam-step-label">
                      {num === 1 ? 'Organization' : num === 2 ? 'Admin Account' : 'Finalize'}
                    </span>
                  </div>
                ))}
              </div>

              <ul className="cam-brand-features mt-5">
                {['Multi-tenant Isolation', 'Role-based Access Control', 'Custom Workspace Settings'].map((f, i) => (
                  <li key={i} className="cam-feature-item">
                    <div className="cam-feature-icon"><Check size={12} color="white" /></div>
                    <span className="cam-feature-text">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="cam-help-center-wrapper">
              <HelpButton type="global" title="Onboarding Guide" variant="text" size={16} openInNewWindow={true} />
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="cam-auth-right">
            <div className="cam-form-container">
              <div className="cam-form-header">
                <h2 className="cam-form-title">
                  {step === 1 ? 'Organization Details' : step === 2 ? 'Administrator Setup' : 'Review & Initialize'}
                </h2>
                <p className="cam-form-subtitle">
                  {step === 1 ? 'Tell us about your company' : step === 2 ? 'Create the primary admin account' : 'Confirm your workspace settings'}
                </p>
              </div>

              {error && (
                <div className="cam-error-alert">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <form onSubmit={step === 3 ? handleSubmit : (e) => e.preventDefault()}>
                
                {/* STEP 1: ORGANIZATION */}
                {step === 1 && (
                  <div className="cam-step-content animate-in fade-in">
                    <div className="cam-form-group">
                      <label className="cam-form-label">Company Name</label>
                      <div className="cam-input-wrapper">
                        <Building size={18} className="cam-input-icon" />
                        <input name="companyName" value={formData.companyName} onChange={handleChange} placeholder="e.g. Nexus Solutions" className="input-cam cam-input-with-icon" required />
                      </div>
                    </div>
                    <div className="cam-form-group">
                      <label className="cam-form-label">Workspace ID (Unique Slug)</label>
                      <div className="cam-input-wrapper">
                        <Lock size={18} className="cam-input-icon" />
                        <input name="companyId" value={formData.companyId} onChange={handleChange} placeholder="e.g. NEXUS-7" className="input-cam cam-input-with-icon" required />
                      </div>
                      <small className="text-muted mt-1">This will be your unique identifier in the system.</small>
                    </div>
                    <div className="cam-form-group">
                      <label className="cam-form-label">Industry Type</label>
                      <select name="companyType" value={formData.companyType} onChange={handleChange} className="input-cam">
                        <option value="service">Service</option>
                        <option value="real_estate">Real Estate</option>
                        <option value="life_sciences">Life Sciences</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* STEP 2: ADMIN ACCOUNT */}
                {step === 2 && (
                  <div className="cam-step-content animate-in fade-in">
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <div className="cam-form-group" style={{ flex: 1 }}>
                        <label className="cam-form-label">First Name</label>
                        <input name="adminFirstName" value={formData.adminFirstName} onChange={handleChange} className="input-cam" required />
                      </div>
                      <div className="cam-form-group" style={{ flex: 1 }}>
                        <label className="cam-form-label">Last Name</label>
                        <input name="adminLastName" value={formData.adminLastName} onChange={handleChange} className="input-cam" required />
                      </div>
                    </div>
                    <div className="cam-form-group">
                      <label className="cam-form-label">Admin Email</label>
                      <div className="cam-input-wrapper">
                        <Mail size={18} className="cam-input-icon" />
                        <input type="email" name="adminEmail" value={formData.adminEmail} onChange={handleChange} className="input-cam cam-input-with-icon" required />
                      </div>
                    </div>
                    <div className="cam-form-group">
                      <label className="cam-form-label">Password</label>
                      <div className="cam-input-wrapper">
                        <Lock size={18} className="cam-input-icon" />
                        <input type={showPassword ? 'text' : 'password'} name="adminPassword" value={formData.adminPassword} onChange={handleChange} className="input-cam cam-input-with-icon cam-input-with-icon-right" required />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="cam-input-icon-right">
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <div className="cam-form-group">
                      <label className="cam-form-label">Phone Number (Digits only)</label>
                      <div className="cam-input-wrapper">
                        <Phone size={18} className="cam-input-icon" />
                        <input name="adminPhone" value={formData.adminPhone} onChange={handleChange} placeholder="e.g. 5550101" className="input-cam cam-input-with-icon" />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: REVIEW */}
                {step === 3 && (
                  <div className="cam-step-content animate-in fade-in">
                    <div className="cam-review-box p-3 rounded mb-4" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <h4 className="mb-3 text-primary" style={{ fontSize: '1rem' }}>Summary</h4>
                      <div className="d-flex flex-column gap-2" style={{ fontSize: '0.9rem' }}>
                        <p><strong>Company:</strong> {formData.companyName} ({formData.companyId})</p>
                        <p><strong>Admin:</strong> {formData.adminFirstName} {formData.adminLastName}</p>
                        <p><strong>Email:</strong> {formData.adminEmail}</p>
                        <p><strong>Type:</strong> {formData.companyType}</p>
                      </div>
                    </div>
                    <p className="cam-form-subtitle" style={{ fontSize: '0.8rem' }}>
                      By clicking initialize, we will set up your database, roles, and admin account.
                    </p>
                  </div>
                )}

                {/* ACTION BUTTONS */}
                <div className="cam-form-actions mt-4 d-flex gap-3">
                  {step > 1 && (
                    <button type="button" onClick={prevStep} className="btn-cam secondary" style={{ flex: 1, backgroundColor: '#64748b' }}>
                      <ChevronLeft size={18} /> Back
                    </button>
                  )}
                  
                  {step < 3 ? (
                    <button type="button" onClick={nextStep} className="btn-cam" style={{ flex: 2 }}>
                      Continue <ChevronRight size={18} />
                    </button>
                  ) : (
                    <button 
                        type="submit" 
                        className="btn-cam" 
                        style={{ flex: 2 }} 
                        disabled={loading} 
                        >
                        {loading ? 'Initializing...' : 'Initialize Workspace'}
                    </button>
                  )}
                </div>

                <div className="cam-form-footer mt-4 text-center">
                  <p className="cam-footer-text">
                    Already have a workspace? <Link to="/login" className="cam-link">Sign In</Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CompanyOnboarding;